import { useAppTranslation } from "@/utils";
import { useInfiniteScroll } from "@/utils/use-infinite-scroll";
import { bnplConsumerApis } from "@payping/api";
import { OrderPaymentStatus } from "@payping/api/app/api/bnplConsumerTypes";
import type { PaymentListResponse } from "@payping/api/app/api/bnplConsumerTypes";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { dateFormats, moment } from "@payping/utils/dateUtils";
import { Divider, Flex, Spin, Tag, Typography, useMessagedAsyncFn } from "@payping/web-components";
import { useEffect, useRef, useState } from "react";

const PaymentHistoryPage = () => {
  const [pageNumber, setPageNumber] = useState(0);
  const { t } = useAppTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollDivRef = useRef<HTMLDivElement>(null);

  const [paymentList, setPaymentList] = useState<PaymentListResponse>({ paymentInfos: [], total: 0 });

  const [{ loading }, getList] = useMessagedAsyncFn(
    async (pgNumber: number) => {
      const res = await bnplConsumerApis.dashboardPaymentList(undefined, undefined, 10, pgNumber);
      setPageNumber(pgNumber);
      setPaymentList((pre) => ({ total: res.total, paymentInfos: [...pre.paymentInfos, ...res.paymentInfos] }));
    },
    [setPageNumber],
    { loading: false },
    { success: false, error: true },
  );

  const effectDependency = [loading, paymentList, pageNumber] as const;

  // only first time
  useEffect(() => {
    const newPageNumber = pageNumber + 1;
    getList(newPageNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useInfiniteScroll<HTMLDivElement, HTMLDivElement, typeof effectDependency>({
    triggerCallback({ effectDependency: eDependency }) {
      const load = eDependency[0];
      const Ilist = eDependency[1];
      const pgN = eDependency[2];

      if (!load && Ilist.paymentInfos.length < (Ilist.total ?? 0)) {
        const newPageNumber = pgN + 1;
        getList(newPageNumber);
      }
    },
    effectDependency,
    scrollTrigger: scrollDivRef,
    scrollWrapper: containerRef,
  });

  return (
    <div ref={containerRef}>
      <Flex vertical gap={"middle"} className="tw:mt-4! tw:w-full tw:p-4!">
        {paymentList.paymentInfos
          .filter((a) => a.paymentStatus !== OrderPaymentStatus.Init)
          .map((item) => (
            <Flex vertical gap={"middle"} className="tw:w-full" key={item.paymentCode}>
              <Flex gap={"middle"} className="tw:w-full" justify="space-between">
                <Typography.Text type="secondary">{`${t("home.paymenyHistory.id")}:`}</Typography.Text>
                <Typography.Text>{item.paymentCode ? item.paymentCode : "-"}</Typography.Text>
              </Flex>
              <Flex gap={"middle"} className="tw:w-full" justify="space-between">
                <Typography.Text type="secondary">{`${t("common.date")}:`}</Typography.Text>
                <Flex gap={"small"}>
                  <Typography.Text>
                    {item.createDate ? moment(item.createDate).format(dateFormats.ui.time) : null}
                  </Typography.Text>
                  <Typography.Text>
                    {item.createDate ? moment(item.createDate).format(dateFormats.ui.jalaliDate) : null}
                  </Typography.Text>
                </Flex>
              </Flex>
              <Flex gap={"middle"} className="tw:w-full" justify="space-between">
                <Typography.Text type="secondary">{`${t("common.amount")}:`}</Typography.Text>
                <Flex>
                  <Typography.Text>{`${formatLocalizedNumber({ value: item.paymentAmount ?? 0 })} `}</Typography.Text>
                  <CuToman width={24} height={24} />
                </Flex>
              </Flex>
              <Flex gap={"middle"} className="tw:w-full" justify="space-between">
                <Typography.Text type="secondary">{`${t("common.status")}:`}</Typography.Text>
                {OrderPaymentStatus.Failed === item.paymentStatus ? (
                  <Tag color="error">{t("home.paymenyHistory.failed")}</Tag>
                ) : null}
                {OrderPaymentStatus.Paid === item.paymentStatus ? (
                  <Tag color="success">{t("home.paymenyHistory.success")}</Tag>
                ) : null}
                {OrderPaymentStatus.Init === item.paymentStatus ? <Tag>{t("home.paymenyHistory.init")}</Tag> : null}
              </Flex>
              <Divider className="tw:m-0!" />
            </Flex>
          ))}

        <Flex className="w-100" justify="center" align="center" vertical>
          {loading ? <Spin /> : null}
          <div ref={scrollDivRef} className="w-100" style={{ height: "1px" }}></div>
        </Flex>
      </Flex>
    </div>
    // </Drawer>
  );
};

export default PaymentHistoryPage;
